import { Attrs, jsonList, scalar } from '@skippay/core/utils/objectUtils';

import {
	AccountInfo,
	BillingInfo,
	CardInfo,
	CashbackInfo,
	CoinsInfo,
	Compliance,
	CurrentApplicationInfo,
	CustomerContext,
	CustomerContextDomain,
	DeferredPaymentItem,
	Eligibility,
	LimitInfo,
	PaymentInstructions,
	ProductLimit,
	RevolvingInfo,
} from './types';

export type { Attrs };

const eligibility = (e?: Eligibility): Attrs =>
	!e
		? {}
		: {
				...scalar('eligibility.canApplyForRevolving', e.canApplyForRevolving),
				...scalar('eligibility.canApplyForInstallments', e.canApplyForInstallments),
				...jsonList('eligibility.availableLoanTypes', e.availableLoanTypes),
			};

const account = (a?: AccountInfo, activeProducts?: CustomerContext['activeProducts']): Attrs => ({
	...scalar('account.package', a?.package),
	...jsonList('account.activeProducts', activeProducts),
});

const compliance = (c?: Compliance): Attrs =>
	!c
		? {}
		: {
				...jsonList('compliance.actionsRequired', c.actionsRequired),
				...jsonList('compliance.blockedCategories', c.blockedCategories),
				...scalar('compliance.kycState', c.kycState),
				...scalar('compliance.documentExpiry', c.documentExpiry),
				...scalar('compliance.isDocumentExpired', c.isDocumentExpired),
				...scalar('compliance.amlReviewExpiresAt', c.amlReviewExpiresAt),
			};

const productLimit = (prefix: string, p?: ProductLimit): Attrs => ({
	...scalar(`${prefix}.source`, p?.source),
	...scalar(`${prefix}.state`, p?.state),
	...scalar(`${prefix}.totalLimit`, p?.totalLimit),
	...scalar(`${prefix}.availableAmount`, p?.availableAmount),
});

const limit = (l?: LimitInfo): Attrs =>
	!l
		? {}
		: {
				...scalar('limit.available', l.available),
				...scalar('limit.total', l.total),
				...scalar('limit.state', l.state),
				...scalar('limit.annualAvailable', l.annualAvailable),
				...scalar('limit.annualTotal', l.annualTotal),
				...productLimit('limit.deferredPayment', l.deferredPayment),
				...productLimit('limit.payInThree', l.payInThree),
				...scalar('limit.payInThree.minimalPurchaseAmount', l.payInThree?.minimalPurchaseAmount),
				...productLimit('limit.revolving', l.revolving),
				...scalar(
					'limit.revolving.limitAmendment.entryState',
					l.revolving?.limitAmendment.entryState
				),
				...scalar(
					'limit.revolving.limitAmendment.maxAllowedEntryLimit',
					l.revolving?.limitAmendment.maxAllowedEntryLimit
				),
				...scalar(
					'limit.revolving.limitAmendment.preIncreaseOffer.available',
					l.revolving?.limitAmendment.preIncreaseOffer.available
				),
			};

const revolving = (r?: RevolvingInfo): Attrs =>
	!r
		? {}
		: {
				...scalar('revolving.state', r.state),
				...scalar('revolving.applicationState', r.applicationState),
				...scalar('revolving.activatedDate', r.activatedDate),
			};

type BillingDomain = Extract<CustomerContextDomain, 'billing' | 'unpaidBilling'>;

const billing = (prefix: BillingDomain, b?: BillingInfo): Attrs =>
	!b
		? {}
		: {
				...scalar(`${prefix}.balance`, b.balance),
				...scalar(`${prefix}.total`, b.total),
				...scalar(`${prefix}.minimumToPay`, b.minimumToPay),
				...scalar(`${prefix}.dueDate`, b.dueDate),
				...scalar(`${prefix}.daysOverdue`, b.daysOverdue),
				...scalar(`${prefix}.isPaid`, b.isPaid),
				...scalar(`${prefix}.canPay`, b.canPay),
				...jsonList(`${prefix}.paymentMethods`, b.paymentMethods),
				...scalar(`${prefix}.canDefer`, b.canDefer),
				...scalar(`${prefix}.period`, b.period),
				...scalar(`${prefix}.paymentDate`, b.paymentDate),
				...jsonList(`${prefix}.whyCannotPay`, b.whyCannotPay),
				...scalar(`${prefix}.deferment.fee`, b.deferment?.fee),
				...scalar(`${prefix}.deferment.feeDiscount`, b.deferment?.feeDiscount),
				...scalar(`${prefix}.deferment.amountToDefer`, b.deferment?.amountToDefer),
				...scalar(`${prefix}.deferment.totalValue`, b.deferment?.totalValue),
				...scalar(`${prefix}.deferment.count`, b.deferment?.count),
				...scalar(`${prefix}.defermentUnavailableReason`, b.defermentUnavailableReason),
				...scalar(`${prefix}.cashback.available`, b.cashback?.available),
				...scalar(`${prefix}.cashback.applied`, b.cashback?.applied),
				...scalar(`${prefix}.cashback.isApplicable`, b.cashback?.isApplicable),
				...scalar(`${prefix}.cashback.isApplied`, b.cashback?.isApplied),
				...scalar(`${prefix}.splitToInstallments.canApply`, b.splitToInstallments?.canApply),
				...scalar(
					`${prefix}.splitToInstallments.maxPrincipal`,
					b.splitToInstallments?.maxPrincipal
				),
				...scalar(`${prefix}.transferBonus.type`, b.transferBonus?.type),
				...scalar(`${prefix}.transferBonus.amount`, b.transferBonus?.amount),
				...scalar(`${prefix}.coinReward`, b.coinReward),
				...jsonList(`${prefix}.campaigns`, b.campaigns),
				...scalar(`${prefix}.unpaidCount`, b.unpaidCount),
			};

const card = (c?: CardInfo, cards?: CardInfo[]): Attrs =>
	!c
		? {}
		: {
				...scalar('card.state', c.state),
				...scalar('card.stateReason', c.stateReason),
				...scalar('card.cardType', c.cardType),
				...jsonList('card.wallets', c.wallets),
				...scalar('card.canShowPin', c.canShowPin),
				...scalar('card.onlinePaymentsEnabled', c.onlinePaymentsEnabled),
				...scalar('card.atmWithdrawalEnabled', c.atmWithdrawalEnabled),
				...scalar('card.canApplyForNewCardIssuance', c.canApplyForNewCardIssuance),
				...scalar('card.validThru', c.validThru),
				...scalar('card.isExpiringSoon', c.isExpiringSoon),
				...jsonList('cards', cards),
			};

const coins = (c?: CoinsInfo): Attrs =>
	!c
		? {}
		: {
				...scalar('coins.balance', c.balance),
				...scalar('coins.nearestExpiration.date', c.nearestExpiration?.date),
				...scalar('coins.nearestExpiration.amount', c.nearestExpiration?.amount),
			};

const cashback = (c?: CashbackInfo): Attrs =>
	!c
		? {}
		: {
				...scalar('cashback.available', c.available),
				...scalar('cashback.pending', c.pending),
				...scalar('cashback.totalApplied', c.totalApplied),
				...scalar('cashback.totalBonus', c.totalBonus),
				...scalar('cashback.nearestExpiration.date', c.nearestExpiration?.date),
				...scalar('cashback.nearestExpiration.amount', c.nearestExpiration?.amount),
			};

const paymentInstructions = (p?: PaymentInstructions): Attrs =>
	!p
		? {}
		: {
				...scalar('paymentInstructions.account', p.account),
				...scalar('paymentInstructions.recipientName', p.recipientName),
				...scalar('paymentInstructions.variableSymbol', p.variableSymbol),
				...scalar('paymentInstructions.referenceNumber', p.referenceNumber),
				...scalar('paymentInstructions.amount', p.amount),
				...scalar('paymentInstructions.debtType', p.debtType),
			};

const withoutPayable = ({
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	payable,
	...item
}: DeferredPaymentItem): Omit<DeferredPaymentItem, 'payable'> => item;

const currentApplication = (a?: CurrentApplicationInfo): Attrs =>
	!a
		? {}
		: {
				...scalar('currentApplication.eshop', a.eshop),
				...scalar('currentApplication.totalPrice', a.totalPrice),
				...jsonList('currentApplication.items', a.items),
				...scalar('currentApplication.financingType', a.financingType),
				...scalar('currentApplication.flowState', a.flowState),
				...scalar('currentApplication.rejectionCategory', a.rejectionCategory),
				...jsonList('currentApplication.installmentPlan', a.installmentPlan),
				...scalar('currentApplication.dueDate', a.dueDate),
				...scalar('currentApplication.canPayByCard', a.canPayByCard),
				...scalar(
					'currentApplication.canApplyForRevolvingWhenRejected',
					a.canApplyForRevolvingWhenRejected
				),
				...scalar('currentApplication.requiredPriceLimit', a.requiredPriceLimit),
				...scalar('currentApplication.unpaidOrdersSum', a.unpaidOrdersSum),
				...scalar('currentApplication.isAuthenticated', a.isAuthenticated),
			};

export const DOMAIN_BUILDERS: Record<CustomerContextDomain, (ctx: CustomerContext) => Attrs> = {
	eligibility: (c) => eligibility(c.eligibility),
	account: (c) => account(c.account, c.activeProducts),
	compliance: (c) => compliance(c.compliance),
	limit: (c) => limit(c.limit),
	revolving: (c) => revolving(c.revolving),
	billing: (c) => billing('billing', c.billing),
	unpaidBilling: (c) => billing('unpaidBilling', c.unpaidBilling),
	payInThree: (c) => jsonList('payInThree', c.payInThree),
	closedPayInThree: (c) => jsonList('closedPayInThree', c.closedPayInThree),
	deferredPayments: (c) => jsonList('deferredPayments', c.deferredPayments?.map(withoutPayable)),
	closedDeferredPayments: (c) => jsonList('closedDeferredPayments', c.closedDeferredPayments),
	installments: (c) => jsonList('installments', c.installments),
	applicationsInProgress: (c) => jsonList('applicationsInProgress', c.applicationsInProgress),
	card: (c) => card(c.card, c.cards),
	coins: (c) => coins(c.coins),
	cashback: (c) => cashback(c.cashback),
	transactions: (c) => jsonList('transactions', c.transactions),
	offers: (c) => jsonList('offers', c.offers),
	outages: (c) => jsonList('outages', c.outages),
	paymentInstructions: (c) => paymentInstructions(c.paymentInstructions),
	currentApplication: (c) => currentApplication(c.currentApplication),
};

export const buildDomainAttrs = (domain: CustomerContextDomain, ctx: CustomerContext): Attrs =>
	DOMAIN_BUILDERS[domain](ctx);

export const buildAllAttrs = (ctx: CustomerContext): Attrs =>
	(Object.keys(DOMAIN_BUILDERS) as CustomerContextDomain[]).reduce(
		(acc, domain) => ({ ...acc, ...buildDomainAttrs(domain, ctx) }),
		{} as Attrs
	);
